import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getCanonicalUrlForPathname,
  getHreflangLinksForPathname,
} from "../../seo/routes";

const managedAttribute = "data-nalosoft-seo";
const legacyManagedAttribute = "data-nalosoft-crawl-seo";
const managedSelector = `[${managedAttribute}="true"], [${legacyManagedAttribute}="true"]`;
const siteName = "Nalosoft";

const appendManagedLink = (attributes: Record<string, string>) => {
  const link = document.createElement("link");
  link.setAttribute(managedAttribute, "true");

  Object.entries(attributes).forEach(([name, value]) => {
    link.setAttribute(name, value);
  });

  document.head.appendChild(link);
};

const appendManagedMeta = (attributes: Record<string, string>) => {
  const meta = document.createElement("meta");
  meta.setAttribute(managedAttribute, "true");

  Object.entries(attributes).forEach(([name, value]) => {
    meta.setAttribute(name, value);
  });

  document.head.appendChild(meta);
};

export default function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    document.head
      .querySelectorAll(managedSelector)
      .forEach((element) => element.remove());

    async function updateHead() {
      const { getPublicRouteEntry } = await import("../../seo/routeMetadata");
      if (cancelled) return;

      const routeEntry = getPublicRouteEntry(location.pathname);
      const canonicalUrl =
        routeEntry?.canonicalUrl ?? getCanonicalUrlForPathname(location.pathname);

      if (!canonicalUrl) {
        appendManagedMeta({ name: "robots", content: "noindex,follow" });
        return;
      }

      if (routeEntry) {
        document.documentElement.lang = routeEntry.language;
        document.title = routeEntry.title;
        appendManagedMeta({
          name: "description",
          content: routeEntry.description,
        });
        appendManagedMeta({ property: "og:type", content: "website" });
        appendManagedMeta({ property: "og:site_name", content: siteName });
        appendManagedMeta({ property: "og:title", content: routeEntry.ogTitle });
        appendManagedMeta({
          property: "og:description",
          content: routeEntry.ogDescription,
        });
        appendManagedMeta({ property: "og:url", content: routeEntry.canonicalUrl });
        appendManagedMeta({ property: "og:image", content: routeEntry.ogImage });
        appendManagedMeta({
          property: "og:image:alt",
          content: routeEntry.ogImageAlt,
        });
        appendManagedMeta({
          property: "og:image:height",
          content: String(routeEntry.ogImageHeight),
        });
        appendManagedMeta({
          property: "og:image:type",
          content: routeEntry.ogImageType,
        });
        appendManagedMeta({
          property: "og:image:width",
          content: String(routeEntry.ogImageWidth),
        });
        appendManagedMeta({ property: "og:locale", content: routeEntry.ogLocale });
        routeEntry.alternateLocales.forEach((locale) => {
          appendManagedMeta({
            property: "og:locale:alternate",
            content: locale,
          });
        });
        appendManagedMeta({ name: "twitter:card", content: routeEntry.twitterCard });
        appendManagedMeta({ name: "twitter:site", content: routeEntry.twitterSite });
        appendManagedMeta({
          name: "twitter:title",
          content: routeEntry.twitterTitle,
        });
        appendManagedMeta({
          name: "twitter:description",
          content: routeEntry.twitterDescription,
        });
        appendManagedMeta({
          name: "twitter:image",
          content: routeEntry.twitterImage,
        });
        appendManagedMeta({
          name: "twitter:image:alt",
          content: routeEntry.twitterImageAlt,
        });
      }

      appendManagedLink({ rel: "canonical", href: canonicalUrl });

      const alternates =
        routeEntry?.alternates.map((alternate) => ({
          hreflang: alternate.hrefLang,
          href: alternate.href,
        })) ?? getHreflangLinksForPathname(location.pathname);

      alternates.forEach((alternate) => {
        appendManagedLink({
          rel: "alternate",
          hreflang: alternate.hreflang,
          href: alternate.href,
        });
      });
    }

    updateHead();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}
